import {
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
  type SortOption,
  type SortOrder,
} from '@/components/layout/Navigation/sessions/header/toolbar/filters/atoms';
import { SESSION_LIST_VIEW_BY_SCOPE_STORAGE_KEY } from '@/constants/sessions';
import { createUserScopedStorageAtom } from '@/lib/jotai-storage/user-scoped';
import { type SessionScope, type SessionStatus } from '@heidi/schema/ml-scribe';
import { atom } from 'jotai';

/**
 * Runtime sort for the new session list. Plain (in-memory) atoms: their value
 * is the active scope's sort, swapped in/out per scope by
 * `useNewSessionListViewPersistence`. Kept separate from the legacy
 * `sessionsSortByAtom` so the old Navigation list is unaffected.
 */
export const newSessionListSortByAtom = atom<SortOption>(DEFAULT_SORT_BY);
export const newSessionListSortOrderAtom = atom<SortOrder>(DEFAULT_SORT_ORDER);

/** A single scope's cached sort + filters (dates stored as ISO strings).
 *  Search and the patient-profile filter are intentionally excluded — search is
 *  a transient query, and the patient filter is deliberately not cached (it
 *  stays a global in-memory selection, never persisted). */
export type SessionListScopeView = {
  sortBy?: SortOption;
  sortOrder?: SortOrder;
  dateRange?: { from?: string; to?: string };
  statuses?: SessionStatus[];
  isUrgentOnly?: boolean;
};

/** Persisted per-scope view cache for the new session list. `byScope` holds each
 *  scope's sort + filters; `clinicianIdsByScope` mirrors the already-scope-keyed
 *  clinician selection. Synced with the runtime atoms by
 *  `useNewSessionListViewPersistence`. */
export type SessionListViewByScope = {
  byScope?: Partial<Record<SessionScope, SessionListScopeView>>;
  clinicianIdsByScope?: Partial<Record<SessionScope, string[]>>;
};

export const newSessionListViewByScopeAtom =
  createUserScopedStorageAtom<SessionListViewByScope>(
    SESSION_LIST_VIEW_BY_SCOPE_STORAGE_KEY,
    {}
  );

/** Bumped by `useNewSessionListViewPersistence` whenever it loads a scope's
 *  cached view into the runtime filter atoms. `useSessionsFilterTracking`
 *  watches it to rebaseline, so a programmatic hydration write isn't mistaken
 *  for a user filter action (which would emit a false `sessions_filter_applied`). */
export const newSessionListViewHydrationSignalAtom = atom(0);
